import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiBox,
  FiRepeat,
  FiSave,
  FiX,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";
import useSwal from "../../../../../../hooks/useSwal";
import "./StockManagement.css";

/* =========================================================
   INITIAL FORM
========================================================= */

const INITIAL_FORM = {
  productId: "",

  actualQty: "",

  referenceNumber: "",

  notes: "",
};


/* =========================================================
   FORMAT NUMBER
========================================================= */

const formatNumber = (
  value
) => {
  return new Intl.NumberFormat(
    "en-IN"
  ).format(
    Number(
      value ||
      0
    )
  );
};


/* =========================================================
   COMPONENT
========================================================= */

const StockReconcileDialog = ({
  open,

  product,

  products = [],

  onClose,

  onSuccess,
}) => {
  const api =
    useMemo(
      () =>
        new API(),
      []
    );

  const swal =
    useSwal();


  /* =======================================================
     STATE
  ======================================================= */

  const [
    form,
    setForm,
  ] =
    useState({
      ...INITIAL_FORM,
    });


  const [
    errors,
    setErrors,
  ] =
    useState({});


  const [
    loading,
    setLoading,
  ] =
    useState(false);


  /* =======================================================
     RESET FORM WHEN OPENING
  ======================================================= */

  useEffect(
    () => {
      if (!open) {
        return;
      }


      setForm({
        ...INITIAL_FORM,

        productId:
          product?._id ||
          "",

        actualQty:
          product
            ?.stockQty ??
          "",
      });


      setErrors(
        {}
      );


      setLoading(
        false
      );

    },
    [
      open,
      product,
    ]
  );


  /* =======================================================
     DON'T RENDER
  ======================================================= */

  if (!open) {
    return null;
  }


  /* =======================================================
     SELECTED PRODUCT

     IMPORTANT:

     Do not blindly fall back to original product
     after user manually changes the dropdown.
  ======================================================= */

  const selectedProduct =
    products.find(
      (
        item
      ) =>
        String(
          item?._id ||
          ""
        ) ===
        String(
          form.productId ||
          ""
        )
    ) ||
    (
      String(
        product?._id ||
        ""
      ) ===
      String(
        form.productId ||
        ""
      )
        ? product
        : null
    ) ||
    null;


  /* =======================================================
     STOCK VALUES
  ======================================================= */

  const currentStock =
    Math.max(
      Number(
        selectedProduct
          ?.stockQty ||
        0
      ),

      0
    );


  const reservedStock =
    Math.max(
      Number(
        selectedProduct
          ?.reservedQty ||
        0
      ),

      0
    );


  const availableStock =
    Math.max(
      currentStock -
        reservedStock,

      0
    );


  /* =======================================================
     ACTUAL QUANTITY
  ======================================================= */

  const actualQty =
    form.actualQty ===
      ""
      ? currentStock
      : Number(
          form.actualQty
        );


  /* =======================================================
     DIFFERENCE
  ======================================================= */

  const difference =
    Number.isFinite(
      actualQty
    )
      ? actualQty -
        currentStock
      : 0;


  const differenceType =
    difference >
    0
      ? "increase"
      : difference <
          0
        ? "decrease"
        : "same";


  /* =======================================================
     AVAILABLE AFTER RECONCILIATION
  ======================================================= */

  const availableAfter =
    Number.isFinite(
      actualQty
    )
      ? Math.max(
          actualQty -
            reservedStock,

          0
        )
      : availableStock;


  /* =======================================================
     COMMON CHANGE
  ======================================================= */

  const handleChange =
    (
      event
    ) => {
      const {
        name,
        value,
      } =
        event.target;


      setForm(
        (
          previous
        ) => ({
          ...previous,

          [name]:
            value,
        })
      );


      if (
        errors[name]
      ) {
        setErrors(
          (
            previous
          ) => ({
            ...previous,

            [name]:
              "",
          })
        );
      }
    };


  /* =======================================================
     PRODUCT CHANGE
  ======================================================= */

  const handleProductChange =
    (
      event
    ) => {
      const productId =
        event.target
          .value;


      const nextProduct =
        products.find(
          (
            item
          ) =>
            String(
              item?._id ||
              ""
            ) ===
            String(
              productId ||
              ""
            )
        );


      setForm(
        (
          previous
        ) => ({
          ...previous,

          productId,

          actualQty:
            nextProduct
              ?.stockQty ??
            "",
        })
      );


      setErrors(
        (
          previous
        ) => ({
          ...previous,

          productId:
            "",

          actualQty:
            "",
        })
      );
    };


  /* =======================================================
     VALIDATION
  ======================================================= */

  const validateForm =
    () => {
      const validationErrors =
        {};


      /* ===================================================
         PRODUCT
      =================================================== */

      if (
        !form.productId
      ) {
        validationErrors.productId =
          "Please select a product.";
      }


      if (
        form.productId &&
        !selectedProduct
      ) {
        validationErrors.productId =
          "Selected product is not available.";
      }


      /* ===================================================
         ACTUAL QUANTITY
      =================================================== */

      const normalizedActualQty =
        Number(
          form.actualQty
        );


      if (
        form.actualQty ===
          "" ||
        !Number.isInteger(
          normalizedActualQty
        ) ||
        normalizedActualQty <
          0
      ) {
        validationErrors.actualQty =
          "Actual quantity must be zero or a positive whole number.";
      }


      /* ===================================================
         RESERVED STOCK PROTECTION

         Reconciliation must never reduce
         stock below reserved quantity.

         Example:

         System stock = 10
         Reserved     = 6

         Actual 8 -> allowed
         Actual 6 -> allowed
         Actual 5 -> rejected
      =================================================== */

      if (
        Number.isInteger(
          normalizedActualQty
        ) &&
        normalizedActualQty <
          reservedStock
      ) {
        validationErrors.actualQty =
          `Actual quantity cannot be lower than reserved quantity ${formatNumber(
            reservedStock
          )}.`;
      }


      /* ===================================================
         NOTES
      =================================================== */

      if (
        String(
          form.notes ||
          ""
        ).length >
        1000
      ) {
        validationErrors.notes =
          "Notes cannot exceed 1000 characters.";
      }


      setErrors(
        validationErrors
      );


      return (
        Object.keys(
          validationErrors
        ).length ===
        0
      );
    };


  /* =======================================================
     SUBMIT
  ======================================================= */

  const handleSubmit =
    async (
      event
    ) => {
      event.preventDefault();


      /*
       * Protect against double submit.
       */

      if (
        loading
      ) {
        return;
      }


      if (
        !validateForm()
      ) {
        return;
      }


      /*
       * Nothing changed.
       *
       * There is no reason to create a
       * zero-quantity reconciliation.
       */

      if (
        difference ===
        0
      ) {
        await swal.warning({
          icon:
            "info",

          title:
            "Stock Already Matched",

          text:
            "System stock already matches the physical stock count.",
        });

        return;
      }


      try {
        setLoading(
          true
        );


        const payload = {
          productId:
            form.productId,


          actualQty:
            Number(
              form.actualQty
            ),


          referenceNumber:
            String(
              form.referenceNumber ||
              ""
            ).trim(),


          notes:
            String(
              form.notes ||
              ""
            ).trim(),
        };


        const response =
          await api.post(
            config
              .reconcileInventoryStock,

            payload
          );


        if (
          response?.status !==
            "success" &&
          response?.success !==
            true
        ) {
          throw new Error(
            response?.message ||
              "Unable to reconcile stock."
          );
        }


        await swal.success({
          icon:
            "success",

          title:
            "Stock Reconciled",

          text:
            response?.message ||
            "Stock reconciled successfully.",

          timer:
            1400,

          showConfirmButton:
            false,
        });


        if (
          typeof onSuccess ===
          "function"
        ) {
          onSuccess(
            response?.data ||
            null
          );
        }

      } catch (
        requestError
      ) {
        console.error(
          "Reconcile stock error:",
          requestError
        );


        await swal.error({
          icon:
            "error",

          title:
            "Unable to Reconcile Stock",

          text:
            requestError
              ?.response
              ?.data
              ?.message ||
            requestError
              ?.message ||
            "Unable to reconcile stock.",
        });

      } finally {
        setLoading(
          false
        );
      }
    };


  /* =======================================================
     CLOSE

     Do not close while reconciliation request
     is in progress.
  ======================================================= */

  const handleClose =
    () => {
      if (
        loading
      ) {
        return;
      }


      onClose?.();
    };


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      className="inventory-stock-dialog-overlay"
    >
      {/* ===================================================
          BACKDROP
      =================================================== */}

      <button
        type="button"
        className="inventory-stock-dialog-backdrop"
        onClick={
          handleClose
        }
        disabled={
          loading
        }
        aria-label="Close stock reconciliation"
      />


      {/* ===================================================
          DIALOG
      =================================================== */}

      <div
        className="inventory-stock-dialog inventory-stock-reconcile-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Reconcile stock"
      >
        {/* =================================================
            HEADER
        ================================================= */}

        <div
          className="inventory-stock-dialog-header"
        >
          <div>
            <span>
              STOCK MANAGEMENT
            </span>


            <h2>
              Reconcile Stock
            </h2>
          </div>


          <button
            type="button"
            className="inventory-stock-dialog-close"
            onClick={
              handleClose
            }
            disabled={
              loading
            }
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>


        {/* =================================================
            FORM
        ================================================= */}

        <form
          onSubmit={
            handleSubmit
          }
        >
          <div
            className="inventory-stock-dialog-body"
          >
            {/* =============================================
                SELECTED PRODUCT SUMMARY
            ============================================= */}

            <div
              className="inventory-stock-adjustment-summary"
            >
              <div
                className="inventory-stock-adjustment-product-icon"
              >
                <FiBox />
              </div>


              <div>
                <span>
                  Selected Product
                </span>


                <strong>
                  {selectedProduct
                    ?.name ||
                    "No product selected"}
                </strong>


                <small>
                  {selectedProduct
                    ?.sku ||
                    "-"}
                </small>
              </div>
            </div>


            {/* =============================================
                CURRENT STOCK SUMMARY
            ============================================= */}

            <div
              className="inventory-stock-current-grid"
            >
              {/* SYSTEM */}

              <div>
                <span>
                  System Stock
                </span>


                <strong>
                  {formatNumber(
                    currentStock
                  )}
                </strong>
              </div>


              {/* RESERVED */}

              <div>
                <span>
                  Reserved
                </span>


                <strong>
                  {formatNumber(
                    reservedStock
                  )}
                </strong>
              </div>


              {/* AVAILABLE */}

              <div>
                <span>
                  Available
                </span>


                <strong>
                  {formatNumber(
                    availableStock
                  )}
                </strong>
              </div>


              {/* ACTUAL */}

              <div>
                <span>
                  Actual Count
                </span>


                <strong>
                  {Number.isFinite(
                    actualQty
                  )
                    ? formatNumber(
                        actualQty
                      )
                    : "-"}
                </strong>
              </div>


              {/* DIFFERENCE */}

              <div>
                <span>
                  Difference
                </span>


                <strong
                  className={
                    differenceType ===
                    "increase"
                      ? "positive"
                      : differenceType ===
                          "decrease"
                        ? "danger"
                        : ""
                  }
                >
                  {difference >
                  0
                    ? `+${formatNumber(
                        difference
                      )}`
                    : formatNumber(
                        difference
                      )}
                </strong>
              </div>


              {/* AVAILABLE AFTER */}

              <div>
                <span>
                  Available After
                </span>


                <strong>
                  {formatNumber(
                    availableAfter
                  )}
                </strong>
              </div>
            </div>


            {/* =============================================
                FORM GRID
            ============================================= */}

            <div
              className="inventory-stock-form-grid"
            >
              {/* ===========================================
                  PRODUCT
              =========================================== */}

              <div
                className="inventory-stock-form-field inventory-stock-form-field-full"
              >
                <label>
                  Product *
                </label>


                <select
                  name="productId"
                  value={
                    form.productId
                  }
                  onChange={
                    handleProductChange
                  }
                  disabled={
                    loading
                  }
                >
                  <option
                    value=""
                  >
                    Select Product
                  </option>


                  {products.map(
                    (
                      item
                    ) => (
                      <option
                        key={
                          item._id
                        }
                        value={
                          item._id
                        }
                      >
                        {item.name}

                        {item.sku
                          ? ` (${item.sku})`
                          : ""}

                        {" — Stock: "}

                        {formatNumber(
                          item.stockQty ||
                          0
                        )}
                      </option>
                    )
                  )}


                  {/* =======================================
                      FALLBACK FOR PRODUCT PASSED DIRECTLY

                      Useful when opening reconcile dialog
                      from Product tab but product isn't in
                      currently loaded Stock product list.
                  ======================================= */}

                  {product?._id &&
                  !products.some(
                    (
                      item
                    ) =>
                      String(
                        item?._id ||
                        ""
                      ) ===
                      String(
                        product._id
                      )
                  ) ? (
                    <option
                      value={
                        product._id
                      }
                    >
                      {product.name}

                      {product.sku
                        ? ` (${product.sku})`
                        : ""}

                      {" — Stock: "}

                      {formatNumber(
                        product.stockQty ||
                        0
                      )}
                    </option>
                  ) : null}
                </select>


                {errors.productId ? (
                  <small>
                    {errors.productId}
                  </small>
                ) : null}
              </div>


              {/* ===========================================
                  SYSTEM QUANTITY
              =========================================== */}

              <div
                className="inventory-stock-form-field"
              >
                <label>
                  System Quantity
                </label>


                <input
                  type="number"
                  value={
                    currentStock
                  }
                  disabled
                />
              </div>


              {/* ===========================================
                  RESERVED QUANTITY
              =========================================== */}

              <div
                className="inventory-stock-form-field"
              >
                <label>
                  Reserved Quantity
                </label>


                <input
                  type="number"
                  value={
                    reservedStock
                  }
                  disabled
                />


                <span
                  className="inventory-stock-field-help"
                >
                  Physical stock cannot be
                  reconciled below this value.
                </span>
              </div>


              {/* ===========================================
                  AVAILABLE QUANTITY
              =========================================== */}

              <div
                className="inventory-stock-form-field"
              >
                <label>
                  Available Quantity
                </label>


                <input
                  type="number"
                  value={
                    availableStock
                  }
                  disabled
                />
              </div>


              {/* ===========================================
                  ACTUAL PHYSICAL QUANTITY
              =========================================== */}

              <div
                className="inventory-stock-form-field"
              >
                <label>
                  Actual Physical Quantity *
                </label>


                <input
                  type="number"
                  name="actualQty"
                  min={
                    reservedStock
                  }
                  step="1"
                  value={
                    form.actualQty
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Enter actual stock"
                  disabled={
                    loading ||
                    !selectedProduct
                  }
                />


                {errors.actualQty ? (
                  <small>
                    {errors.actualQty}
                  </small>
                ) : (
                  <span
                    className="inventory-stock-field-help"
                  >
                    Enter the exact physical
                    count. Minimum allowed:{" "}
                    {formatNumber(
                      reservedStock
                    )}
                  </span>
                )}
              </div>


              {/* ===========================================
                  REFERENCE NUMBER
              =========================================== */}

              <div
                className="inventory-stock-form-field inventory-stock-form-field-full"
              >
                <label>
                  Reference Number
                </label>


                <input
                  type="text"
                  name="referenceNumber"
                  value={
                    form.referenceNumber
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="STOCK-AUDIT-2026-001"
                  disabled={
                    loading
                  }
                />
              </div>


              {/* ===========================================
                  NOTES
              =========================================== */}

              <div
                className="inventory-stock-form-field inventory-stock-form-field-full"
              >
                <label>
                  Notes
                </label>


                <textarea
                  name="notes"
                  rows={
                    4
                  }
                  value={
                    form.notes
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Reason for reconciliation, physical verification details, damaged/missing stock notes..."
                  disabled={
                    loading
                  }
                  maxLength={
                    1000
                  }
                />


                <div
                  className="inventory-stock-notes-meta"
                >
                  {errors.notes ? (
                    <small>
                      {errors.notes}
                    </small>
                  ) : (
                    <span />
                  )}


                  <span>
                    {String(
                      form.notes ||
                      ""
                    ).length}
                    /1000
                  </span>
                </div>
              </div>
            </div>


            {/* =============================================
                RECONCILIATION PREVIEW
            ============================================= */}

            <div
              className={`inventory-stock-reconcile-preview ${differenceType}`}
            >
              <FiRepeat />


              <div>
                <strong>
                  {differenceType ===
                  "increase"
                    ? "Stock will increase"
                    : differenceType ===
                        "decrease"
                      ? "Stock will decrease"
                      : "No stock change"}
                </strong>


                <span>
                  System stock:{" "}
                  {formatNumber(
                    currentStock
                  )}

                  {" • "}

                  Actual stock:{" "}

                  {Number.isFinite(
                    actualQty
                  )
                    ? formatNumber(
                        actualQty
                      )
                    : "-"}

                  {" • "}

                  Adjustment:{" "}

                  {difference >
                  0
                    ? `+${formatNumber(
                        difference
                      )}`
                    : formatNumber(
                        difference
                      )}

                  {" • "}

                  Available after:{" "}

                  {formatNumber(
                    availableAfter
                  )}
                </span>
              </div>
            </div>
          </div>


          {/* =================================================
              FOOTER
          ================================================= */}

          <div
            className="inventory-stock-dialog-footer"
          >
            <button
              type="button"
              className="inventory-stock-dialog-cancel"
              onClick={
                handleClose
              }
              disabled={
                loading
              }
            >
              Cancel
            </button>


            <button
              type="submit"
              className="inventory-stock-dialog-save inventory-stock-reconcile-save"
              disabled={
                loading ||
                !selectedProduct ||
                difference ===
                  0
              }
            >
              <FiSave />


              {loading
                ? "Reconciling..."
                : difference ===
                    0
                  ? "Stock Already Matched"
                  : "Reconcile Stock"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


export default StockReconcileDialog;